import React from 'react';
import { REVIEWS, RATINGS, WHATSAPP_LINK, HERO_IMAGE } from '../data/mockData';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';

const StarRating = ({ count }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={`star-${i}`} size={13} className={i < count ? 'fill-[#D4A843] text-[#D4A843]' : 'text-[#3A3A3A]'} />
    ))}
  </div>
);

const ReviewCard = ({ review, index, inView }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={inView ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className="bg-[#1A1A1A] rounded-2xl p-6 border border-[#222222] hover:border-[#D4A843]/20 transition-all duration-500 flex flex-col"
  >
    <StarRating count={review.stars} />
    <p className="text-[#B0A898] text-sm leading-relaxed mt-4 mb-5 flex-1 italic">
      &ldquo;{review.text}&rdquo;
    </p>
    <div className="flex items-center gap-3 border-t border-[#222222] pt-4">
      <div className="w-8 h-8 rounded-full bg-[#D4A843]/15 flex items-center justify-center shrink-0">
        <span className="text-[#D4A843] text-xs font-bold">{review.name[0]}</span>
      </div>
      <div>
        <p className="text-[#F5F0E8] text-sm font-medium">{review.name}</p>
        <p className="text-[#5A5040] text-xs">{review.context} &middot; {review.date}</p>
      </div>
    </div>
  </motion.div>
);

const ReviewsPage = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <Layout>
      <PageHeader
        title="What the"
        titleAccent="MISFiTS say."
        subtitle="Real reviews from real guests who found their people here."
        bgImage={HERO_IMAGE}
      />

      <section className="pb-24 px-6 lg:px-8 bg-[#111111]">
        <div ref={ref} className="max-w-7xl mx-auto">
          {/* Rating Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14 max-w-2xl mx-auto">
            {[
              { label: 'Overall Rating', value: RATINGS.overall },
              { label: 'Cleanliness', value: RATINGS.cleanliness },
              { label: 'Vibe & Atmosphere', value: RATINGS.vibe },
              { label: 'Staff & Service', value: RATINGS.staff },
            ].map((stat) => (
              <div key={stat.label} className="text-center bg-[#1A1A1A] rounded-xl py-4 px-3 border border-[#222222]">
                <span className="text-2xl font-bold text-[#D4A843] font-heading">{stat.value}</span>
                <p className="text-[#6A6050] text-[10px] uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {REVIEWS.map((review, i) => (
              <ReviewCard key={review.name} review={review} index={i} inView={inView} />
            ))}
          </div>

          <div className="text-center mt-14">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#D4A843] text-[#1C1C1C] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#E8B940] transition-colors duration-300 group/btn">
              Book Your Stay <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReviewsPage;
